import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Users, Video, Clock, CheckSquare, Bell, BookOpen, ArrowRight, HelpCircle, History, MessageSquare, Calendar, Shield } from "lucide-react";
import { RelatedAlternatives } from "@/components/RelatedAlternatives";

const YahooGroups = () => {
  const faqData = [
    {
      question: "What happened to Yahoo Groups?",
      answer: "Yahoo Groups was discontinued on December 15, 2020. Yahoo announced the shutdown in October 2019, giving users over a year to migrate their content. The platform had been declining in usage for years as users moved to social media and other community platforms."
    },
    {
      question: "Why did Yahoo Groups shut down?",
      answer: "Yahoo Groups shut down due to declining user engagement, the rise of social media platforms like Facebook Groups, and Yahoo's strategic focus on other products. The platform struggled to compete with modern community tools and required significant maintenance costs."
    },
    {
      question: "What is the best Yahoo Groups alternative for study groups?",
      answer: "CramStudyWith.me is an excellent alternative for educational communities. It offers Study Stream for video collaboration, group study rooms, task management, and productivity tools specifically designed for learning and academic collaboration."
    },
    {
      question: "Can I recreate my Yahoo Groups community elsewhere?",
      answer: "Yes! CramStudyWith.me provides modern community features including video study rooms, shared task management, and collaborative learning tools. You can create study groups and invite members to join focused learning communities."
    },
    {
      question: "Is CramStudyWith.me free to use?",
      answer: "Yes, CramStudyWith.me offers free access to core features including Study Stream, Study Timer, Task Management, and group study capabilities. It's a comprehensive platform for students and learners seeking community-based study tools."
    },
    {
      question: "What features does CramStudyWith.me offer for group collaboration?",
      answer: "CramStudyWith.me offers Study Stream for live video sessions, group study rooms for collaborative learning, task management for organizing group projects, reminders for deadlines, and productivity timers to keep groups focused and accountable."
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Yahoo Groups Alternative - Free Study Rooms | CramStudyWith.me",
    "description": "Looking for a Yahoo Groups alternative? Yahoo Groups is discontinued but CramStudyWith.me offers free Study Stream, Study Timer, and Task Management for community learning.",
    "url": "https://cramstudywith.me/yahoo-groups/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CramStudyWith.me",
      "url": "https://cramstudywith.me"
    },
    "about": {
      "@type": "Thing",
      "name": "Yahoo Groups Alternative"
    },
    "mentions": {
      "@type": "SoftwareApplication",
      "name": "Yahoo Groups",
      "applicationCategory": "CommunicationApplication"
    },
    "keywords": "Yahoo Groups alternative, Yahoo Groups replacement, Yahoo Groups shutdown, online community platform, study groups, group collaboration"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CramStudyWith.me",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "ratingCount": "1000"
    },
    "featureList": [
      "Study Stream - Live video study sessions",
      "Study Timer - Pomodoro and custom timers",
      "Task Management - Organize assignments",
      "Group Study Rooms - Collaborative learning",
      "Reminders - Never miss deadlines",
      "Flash Cards - Active recall learning",
      "Quizzes - Test your knowledge",
      "Community Features - Study together"
    ],
    "publisher": {
      "@type": "Organization",
      "name": "CramStudyWith.me"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Yahoo Groups Alternative - Free Study Rooms | CramStudyWith.me</title>
        <meta name="description" content="Looking for a Yahoo Groups alternative? Yahoo Groups is discontinued but CramStudyWith.me offers free Study Stream, Study Timer, and Task Management for community learning." />
        <meta name="keywords" content="Yahoo Groups alternative, Yahoo Groups replacement, Yahoo Groups shutdown, online community platform, study groups, group collaboration, CramStudyWith.me" />
        <link rel="canonical" href="https://cramstudywith.me/yahoo-groups/" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
          <div className="container mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full mb-6">
              <MessageSquare className="h-4 w-4" />
              <span className="text-sm font-medium">Yahoo Groups Discontinued December 2020</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Yahoo Groups Alternative
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Yahoo Groups is no longer available. Discover CramStudyWith.me - the modern platform for <Link to="/study-with-me/" className="text-primary hover:underline">study communities</Link> with video collaboration, group study rooms, and productivity tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/studystream/">
                <Button size="lg" className="gap-2">
                  <Video className="h-5 w-5" />
                  Try Study Stream Free
                </Button>
              </Link>
              <Link to="/yahoo-groups/history/">
                <Button size="lg" variant="outline" className="gap-2">
                  <History className="h-5 w-5" />
                  Yahoo Groups History
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* What Happened Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-6 text-center">What Happened to Yahoo Groups?</h2>
              <div className="bg-card rounded-lg p-8 shadow-lg">
                <p className="text-lg text-muted-foreground mb-4">
                  Yahoo Groups, one of the internet's original community platforms, was <strong>permanently discontinued on December 15, 2020</strong>. Launched in 2001 after Yahoo acquired eGroups, the platform served millions of online communities for nearly two decades.
                </p>
                <p className="text-lg text-muted-foreground mb-4">
                  The shutdown came after years of declining usage as users migrated to social media platforms and modern community tools. Yahoo gave users over a year of notice to archive their content before the final shutdown.
                </p>
                <p className="text-lg text-muted-foreground">
                  If you're looking for a replacement for study groups and educational communities, <Link to="/online-workspace/" className="text-primary hover:underline">CramStudyWith.me</Link> offers modern features including <Link to="/productivity-tool/" className="text-primary hover:underline">productivity tools</Link>, video collaboration, and <Link to="/study-with-friends/" className="text-primary hover:underline">group study capabilities</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Comparison */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-12 text-center">Yahoo Groups vs CramStudyWith.me</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Video className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Study Stream</CardTitle>
                  <CardDescription>Live video study sessions</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Unlike Yahoo Groups' text-based discussions, <Link to="/studystream/" className="text-primary hover:underline">Study Stream</Link> enables real-time video collaboration for immersive community learning experiences.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Users className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Group Study Rooms</CardTitle>
                  <CardDescription>Virtual study spaces</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Create focused <Link to="/group-study/" className="text-primary hover:underline">study groups</Link> with video, audio, and collaborative tools - a modern evolution of Yahoo Groups' community spaces.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Clock className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Study Timer</CardTitle>
                  <CardDescription>Pomodoro technique</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Stay focused with our <Link to="/study-timer/" className="text-primary hover:underline">Study Timer</Link> featuring customizable Pomodoro sessions and productivity tracking for individuals and groups.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <CheckSquare className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Task Management</CardTitle>
                  <CardDescription>Organize group projects</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Manage group assignments and projects with our <Link to="/task-management/" className="text-primary hover:underline">Task Management</Link> system - more powerful than Yahoo Groups' basic file sharing.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Bell className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Smart Reminders</CardTitle>
                  <CardDescription>Never miss deadlines</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Get intelligent <Link to="/reminders/" className="text-primary hover:underline">reminders</Link> for study sessions and deadlines - improved notification system compared to Yahoo Groups' email digests.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <BookOpen className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Learning Tools</CardTitle>
                  <CardDescription>Flash cards & quizzes</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Access <Link to="/flash-cards/" className="text-primary hover:underline">flash cards</Link> and <Link to="/quizzes/" className="text-primary hover:underline">quizzes</Link> for active learning - educational features Yahoo Groups never offered.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Blog Links Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">Learn More About Productive Studying</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Link to="/blog/how-turning-on-my-camera-boosted-my-productivity/" className="group">
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">How Turning On My Camera Boosted My Productivity</CardTitle>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/blog/top-10-digital-tools-for-academic-success/" className="group">
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">Top 10 Digital Tools for Academic Success</CardTitle>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/blog/science-behind-study-with-me/" className="group">
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">The Science Behind Study With Me</CardTitle>
                  </CardHeader>
                </Card>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-center gap-3 mb-8">
                <HelpCircle className="h-8 w-8 text-primary" />
                <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Frequently Asked Questions
                </h2>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {faqData.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary to-secondary">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Find Your New Study Community?
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
              Join thousands of students using CramStudyWith.me for collaborative learning and community study sessions.
            </p>
            <Link to="/studystream/">
              <Button size="lg" variant="secondary" className="gap-2">
                Get Started Free
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <RelatedAlternatives currentPlatform="Yahoo Groups" />

      <Footer />
    </div>
  );
};

export default YahooGroups;